import PropTypes from "prop-types";
import React, { useContext, useEffect, useRef, useState } from "react";

import { getCumulativeOffset, useDeviceInfo, useResizeObserver, window } from "@swa-ui/browser";
import { classNames } from "@swa-ui/string";

import { AriaLive } from "../AriaLive";
import { ConfigurationContext } from "../ConfigurationContext";
import { keyCodes } from "../defines/keyCodes";
import { HorizontalGestureScroller } from "../HorizontalGestureScroller";
import { List, listPropTypes } from "../List";
import { PositionIndicator } from "../PositionIndicator";
import styles from "./ListScroller.module.scss";

const EVENT_AGGREGATE_DURATION = 333;
const FOCUS_DELAY = 200;

/**
 * ListScroller provides a way to horizontally scroll through a list of items using gestures,
 * navigation keys like arrows, and/or left/right buttons.
 *
 * Note: ListScroller is intended to be rendered "full-bleed", and not in a grid with margins.
 */

export const ListScroller = React.memo((props) => {
  const {
    center,
    className,
    itemClassName,
    items,
    layout,
    numberOfColumns,
    onClick,
    showPositionIndicator,
    yArrowOffset,
  } = props;
  const { gridColumnSpacing, gridPadding, isTouchDevice, screenSize } = useDeviceInfo();
  const [containerWidth, setContainerWidth] = useState(0);
  const [itemWidths, setItemWidths] = useState([]);
  const [pageIndexes, setPageIndexes] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [open, setOpen] = useState();
  const [scrollPosition, setScrollPosition] = useState(0);
  const configurationContext = useContext(ConfigurationContext);
  const containerRef = useRef();
  const openedCaption = useRef(false);

  useResizeObserver({
    callback: () => setContainerWidth(containerRef?.current?.getBoundingClientRect().width),
    delay: EVENT_AGGREGATE_DURATION,
    element: containerRef,
  });

  useEffect(() => {
    setScrollPosition(0);
    setCurrentPage(0);
    setPageIndexes(getPageIndexes());
  }, [containerWidth, items]);

  useEffect(() => {
    setPageIndexes(getPageIndexes());
  }, [itemWidths.length]);

  useEffect(() => {
    const nextPage = Math.min(currentPage + 1, pageIndexes.length);
    const nextPageScrollPosition = getPageScrollPosition(nextPage);
    const previousPage = Math.max(currentPage - 1, 0);
    const previousPageScrollPosition = getPageScrollPosition(previousPage);

    if (scrollPosition <= nextPageScrollPosition) {
      setCurrentPage(nextPage);
    } else if (scrollPosition >= previousPageScrollPosition) {
      setCurrentPage(previousPage);
    } else if (scrollPosition <= getFarthestScrollPosition() + gridColumnSpacing) {
      setCurrentPage(pageIndexes.length - 1);
    }
  }, [scrollPosition]);

  return (
    <div {...getProps()}>
      <ConfigurationContext.Provider value={getContextValue()}>
        <div {...getScrollerContainerProps()}>
          <HorizontalGestureScroller {...getScrollerProps()}>
            {containerWidth !== 0 && <List {...getListProps()} />}
          </HorizontalGestureScroller>
          {renderLocationIndicator()}
        </div>
      </ConfigurationContext.Provider>
      {renderAriaLabel()}
    </div>
  );

  function renderAriaLabel() {
    return (
      <AriaLive>
        {`${props["aria-label-page-count"]} ${pageIndexes.length}. ${
          props["aria-label-current-page"]
        } ${currentPage + 1}.`}
      </AriaLive>
    );
  }

  function renderLocationIndicator() {
    return (
      showPositionIndicator &&
      !isTouchDevice &&
      pageIndexes.length > 1 && <PositionIndicator {...getPositionIndicatorProps()} />
    );
  }

  function renderListOption(option, index) {
    const { item } = option;
    const optionAriaLabel = option["aria-label"];

    return {
      "aria-label": optionAriaLabel,
      item: <div {...getItemProps(index)}>{item ?? option}</div>,
    };
  }

  function getProps() {
    return {
      "aria-atomic": false,
      "aria-label": props["aria-label"],
      "aria-labelledby": props["aria-labelledby"],
      "aria-live": "polite",
      "aria-roledescription": "carousel",
      className: getClass(),
    };
  }

  function getItemProps(index) {
    return {
      "aria-roledescription": "slide",
      className: classNames(styles.listScrollerItem),
      ref: registerItemWidth(index),
      style: layout === "grid" ? getStyle() : undefined,
    };
  }

  function getScrollerContainerProps() {
    return {
      className: styles.scroller,
      onKeyDown: handleKeyDown,
      ref: containerRef,
      tabIndex: -1,
    };
  }

  function getScrollerProps() {
    return {
      ...(center && { alignment: "center" }),
      "aria-label-focus-change-next": props["aria-label-focus-change-next"],
      "aria-label-focus-change-previous": props["aria-label-focus-change-previous"],
      "aria-label-next": props["aria-label-next"],
      "aria-label-previous": props["aria-label-previous"],
      className: styles.horizontalGestureScroller,
      hasButtons: "auto",
      moreContentIndicator: !open && isTouchDevice,
      onClick: onClick,
      onFocus: handleFocus,
      onMove: handleMove,
      yArrowOffset: yArrowOffset,
    };
  }

  function getListProps() {
    return {
      animateInitialRender: true,
      animationToken: `${items.length}-${containerWidth}`,
      className: styles.list,
      fullWidthContent: true,
      itemClassName,
      items: items.map(renderListOption),
      maxItemsToDisplay: items.length,
    };
  }

  function getPositionIndicatorProps() {
    return {
      className: styles.positionIndicator,
      position: currentPage,
      positionCount: pageIndexes.length,
    };
  }

  function getClass() {
    return classNames(className, styles.listScrollerContainer);
  }

  function getContextValue() {
    return {
      ...configurationContext,
      ...{ handleCaption: [...(configurationContext?.handleCaption ?? []), handleCaption] },
    };
  }

  function handleCaption(captionInfo) {
    const { adjoinedContent, open: opened } = captionInfo;

    openedCaption.current = opened;
    window.setTimeout(() => {
      setOpen(opened ? adjoinedContent : undefined);
    }, FOCUS_DELAY);
  }

  function handleFocus(event) {
    const indexListItem = getIndexOfItemContainingElement(event.target);
    const offsetListItem = getOffsetForItemIndex(indexListItem);

    if (offsetListItem + itemWidths[indexListItem] + scrollPosition > containerWidth) {
      const offset = offsetListItem + itemWidths[indexListItem];

      window.setTimeout(() => {
        !openedCaption.current && setScrollPosition(containerWidth - offset);
      }, FOCUS_DELAY);
    } else if (
      indexListItem < getIndexFirstVisibleItem() ||
      (indexListItem === getIndexFirstVisibleItem() && offsetListItem < -scrollPosition)
    ) {
      window.setTimeout(() => {
        !openedCaption.current && setScrollPosition(-offsetListItem);
      }, FOCUS_DELAY);
    }
  }

  function getIndexOfItemContainingElement(element) {
    const offsetElement = getCumulativeOffset(element, containerRef.current).left;

    return getIndexForOffset(offsetElement);
  }

  function handleKeyDown(event) {
    const { key } = event;
    let index;
    let offset;

    if (key === keyCodes.KEY_RIGHT) {
      index = getIndexFirstInvisibleItemOnRight();
      offset = getOffsetForItemIndex(index);
      setScrollPosition(-(offset + itemWidths[index] - containerWidth));
    } else if (key === keyCodes.KEY_LEFT) {
      index = Math.max(getIndexFirstVisibleItem() - 1, 0);
      offset = getOffsetForItemIndex(index);
      setScrollPosition(-offset);
    } else if (key === keyCodes.KEY_PAGE_DOWN) {
      index = getIndexFirstInvisibleItemOnRight();
      offset = getOffsetForItemIndex(index);
      setScrollPosition(-offset);
    } else if (key === keyCodes.KEY_PAGE_UP) {
      index = getIndexFirstVisibleItem();
      offset = getOffsetForItemIndex(Math.max(index - getNumberItemsPerPage(), 0));
      setScrollPosition(-offset);
    } else if (key === keyCodes.KEY_HOME) {
      setScrollPosition(0);
    } else if (key === keyCodes.KEY_END) {
      setScrollPosition(-(getTotalWidthAllItems() - containerWidth));
    }
  }

  function getFarthestScrollPosition() {
    return containerWidth - gridColumnSpacing - getTotalWidthAllItems();
  }

  function getPageScrollPosition(pageNumber) {
    const pagesToScroll = itemWidths.slice(0, pageIndexes[pageNumber]);
    let pageScrollPosition;

    if (pageNumber === pageIndexes.length - 1) {
      pageScrollPosition = getFarthestScrollPosition();
    } else {
      pageScrollPosition = pagesToScroll.reduce(
        (offset, itemWidth) => offset - (itemWidth + gridColumnSpacing),
        0
      );
    }

    return pageScrollPosition;
  }

  function getStyle() {
    const columnsInRow = getNumberOfColumnsForDevice();

    const spaceUsedByMargins = (columnsInRow - 1) * gridColumnSpacing;
    let columnWidth = (containerWidth - spaceUsedByMargins) / columnsInRow;

    if (columnsInRow === 1) {
      columnWidth -= gridPadding;
    }

    return {
      width: `${columnWidth}px`,
    };
  }

  function getPageIndexes() {
    let pageWidth = 0;

    return itemWidths.reduce((newPageIndexes, itemWidth, index) => {
      const totalItemWidth = itemWidth + gridColumnSpacing;
      const pagePlusNextItemWidth = pageWidth + totalItemWidth;

      if (index === 0 || pagePlusNextItemWidth > containerWidth) {
        newPageIndexes.push(index);
        pageWidth = totalItemWidth;
      } else {
        pageWidth = pagePlusNextItemWidth;
      }

      return newPageIndexes;
    }, []);
  }

  function getIndexFirstInvisibleItemOnRight() {
    let sum = 0;

    const index = itemWidths.findIndex((width) => {
      sum += width + gridColumnSpacing;

      return -scrollPosition + containerWidth < sum;
    });

    return index > 0 ? index : items.length - 1;
  }

  function handleMove(scrollOffset) {
    setScrollPosition(scrollOffset);
  }

  function getIndexFirstVisibleItem() {
    let sum = 0;

    return itemWidths.findIndex((width) => {
      sum += width + gridColumnSpacing;

      return scrollPosition > -sum;
    });
  }

  function getIndexForOffset(offset, inclusive) {
    let width = 0;
    const index = itemWidths.findIndex((itemOffset) => {
      width += itemOffset + gridColumnSpacing;

      return inclusive ? width >= offset : width > offset;
    });

    return index === -1 ? (offset > getTotalWidthAllItems() ? items.length - 1 : 0) : index;
  }

  function getOffsetForItemIndex(index) {
    const widths = index > 0 ? itemWidths.slice(0, index) : [];

    return widths.reduce((partialSum, width) => partialSum + width + gridColumnSpacing, 0);
  }

  function getTotalWidthAllItems() {
    return (
      itemWidths.reduce((totalWidth, width) => totalWidth + width + gridColumnSpacing, 0) -
      gridColumnSpacing
    );
  }

  function getNumberItemsPerPage() {
    let numberItemsPerPage = 0;

    if (containerWidth && itemWidths?.length) {
      numberItemsPerPage = itemWidths.length / pageIndexes.length;
    }

    return numberItemsPerPage;
  }

  function getNumberOfColumnsForDevice() {
    const columnsByScreenSize = {
      large: 4,
      medium: 3,
      small: 1,
      xlarge: 4,
    };

    return numberOfColumns ?? columnsByScreenSize[screenSize];
  }

  function registerItemWidth(index) {
    return (element) => {
      if (element) {
        setItemWidths((previousItemWidths) => {
          previousItemWidths[index] = element.getBoundingClientRect().width;

          return [...previousItemWidths];
        });
      }
    };
  }
});

ListScroller.propTypes = {
  /** aria-label text to provide accessibility description. */
  "aria-label": PropTypes.string,

  /**
   * Content to be displayed to indicate current page - will be followed by the current page number.
   * This should be something like "Current page is".
   */
  "aria-label-current-page": PropTypes.string.isRequired,

  /**
   * Content that will be included in the announcement when focus is moved to the next page button.
   */
  "aria-label-focus-change-next": PropTypes.string.isRequired,

  /**
   * Content that will be included in the announcement when focus is moved to the previous page button.
   */
  "aria-label-focus-change-previous": PropTypes.string.isRequired,

  /** aria-label text to provide accessibility description for previous page button. */
  "aria-label-next": PropTypes.string.isRequired,

  /**
   * Content to be displayed to indicate total number of pages - will be followed by the total
   * page count. This should be something like "Total number of pages".
   */
  "aria-label-page-count": PropTypes.string.isRequired,

  /** aria-label text to provide accessibility description for increment button. */
  "aria-label-previous": PropTypes.string.isRequired,

  /**
   * aria-labelledby id to element which provides additional accessibility description of input
   * element.
   */
  "aria-labelledby": PropTypes.string,

  /**
   * Explicitly center items in the window horizontally. Set this prop to true when the item(s) will
   * not fill the entire window's width.
   */
  center: PropTypes.bool,

  /**
   * Additional classes for positioning the component. Given classes may only position this
   * component for layout purposes, and cannot change how the component renders in any way.
   */
  className: PropTypes.string,

  /** Class to be added to each item's container. */
  itemClassName: listPropTypes.itemClassName,

  /**
   * Content List to be displayed. Must provide an aria-label for each item to describe its
   * location in the list. See List component for more details.
   */
  items: listPropTypes.items,

  /**
   * When displaying items, the items can be laid out in a grid that's appropriately defined for
   * each device size. Only the values used for gaps between items will utilize grid values. The
   * item width will be determine by whatever space is left over. If the numberOfColumns specified
   * match the count of columns appropriate for the device, then the items will match standard grids.
   * If layout is none, all items sizes and horizontal gaps between items must be defined by the
   * calling component.
   */
  layout: PropTypes.oneOf(["grid", "none"]),

  /**
   * Number of columns. One will always be applied for small (mobile) devices. If numberOfColumns not
   * given, the number of columns will be deduced from the device size.
   */
  numberOfColumns: PropTypes.number,

  /** Callback that will be called when next or previous button is clicked. */
  onClick: PropTypes.func,

  /** Indicates if the position indicator key will be rendered. */
  showPositionIndicator: PropTypes.bool,

  /**
   * Allows the left and right buttons to be positioned manually from the top of the container.
   * if yArrowOffset is not given, the arrow buttons will be centered vertically.
   */
  yArrowOffset: PropTypes.number,
};

ListScroller.displayName = "ListScroller";

ListScroller.defaultProps = {
  center: false,
  layout: "grid",
  showPositionIndicator: true,
};
